import { Layer, Point, Line, Polygon } from '../../context/context';
import { Fleet } from '../../components/map/components/FleetMenu';
import type { FixedRoute, FixedRouteStop } from '../../types/fixedRoutes';
import { isFixedRouteLine } from '../../types/fixedRoutes';
import { serializeFleetToKml, combineKmlData } from './fleetKmlGenerator';
import { serializeFixedRoutesToKml, combineKmlDataWithFixedRoutes } from './fixedRoutesKmlGenerator';

// Legacy data structure types (matching old system)
interface LegacyPlaceMark {
  id?: string;  // Shape ID - must persist for originPointId/destPointId references
  label: string;
  type: "point" | "polygon" | "line";
  latitude?: number;
  longitude?: number;
  geometry?: [number, number][];
  layerId: number;
  // Color and style properties
  colour?: string;
  borderColour?: string;
  opacity?: string;
  borderWidth?: number;
  icon?: string;
  size?: number;
  // Ride/trip-specific fields (for line type)
  time?: string | number | null;
  request_time?: string | null;
  numPassengers?: number | string | null;
  wheelchair?: number | string | null;
  ride_id?: string;
  user_id?: string;
  standard_seat?: number;
  request_type?: string;
  originPointId?: string;
  destPointId?: string;
  _timeType?: 'string' | 'number';
}

interface LegacyLayer {
  layerName: string;
  placeMarks: LegacyPlaceMark[];
}

/**
 * Convert current Layer structure to legacy placeMarks structure
 */
function convertToLegacyLayers(layers: Layer[]): LegacyLayer[] {
  return layers.map(layer => {
    const placeMarks: LegacyPlaceMark[] = [];
    
    layer.shapes
      .filter(shape => shape.type !== 'raster') // Exclude raster shapes
      // Exclude derived fixed-route lines: they are reconstructed from the
      // separately-persisted "Fixed Routes Configuration" on load (see
      // serializeFixedRoutesToKml + useFixedRoutesSync). Serializing them here too
      // would round-trip them as plain placemarks, so every optimised route would
      // appear twice in the layer panel after save/load.
      .filter(shape => !(shape.type === 'line' && isFixedRouteLine(shape as Line)))
      .forEach(shape => {
        const baseMark = {
          id: shape.id,
          label: shape.name || 'Unnamed',
          layerId: layer.layerId
        };

        if (shape.type === 'point') {
          // Validate point coordinates
          if (!shape.coordinates || shape.coordinates.length < 2) {
            console.warn(`⚠️ Skipping point "${shape.name}" - invalid coordinates:`, shape.coordinates);
            return;
          }
          const lat = shape.coordinates[0];
          const lng = shape.coordinates[1];
          if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
            console.warn(`⚠️ Skipping point "${shape.name}" - invalid lat/lng:`, lat, lng);
            return;
          }
          const point = shape as Point;
          placeMarks.push({
            ...baseMark,
            type: "point" as const,
            latitude: lat,
            longitude: lng,
            colour: point.colour,
            icon: point.icon,
            size: point.size
          });
        } else if (shape.type === 'polygon') {
          // Validate polygon coordinates
          if (!shape.coordinates || shape.coordinates.length === 0 || !shape.coordinates[0]) {
            console.warn(`⚠️ Skipping polygon "${shape.name}" - invalid coordinates`);
            return;
          }
          const geometry = shape.coordinates[0]; // First ring only for legacy compatibility
          if (!Array.isArray(geometry) || geometry.length === 0) {
            console.warn(`⚠️ Skipping polygon "${shape.name}" - invalid geometry`);
            return;
          }
          const polygon = shape as Polygon;
          placeMarks.push({
            ...baseMark,
            type: "polygon" as const,
            geometry,
            colour: polygon.colour,
            borderColour: polygon.borderColour,
            opacity: polygon.opacity,
            borderWidth: polygon.borderWidth
          });
        } else if (shape.type === 'line') {
          // Validate line coordinates
          if (!shape.coordinates || shape.coordinates.length === 0) {
            console.warn(`⚠️ Skipping line "${shape.name}" - invalid coordinates`);
            return;
          }
          // Keep lines as lines (not converting to polygons)
          const line = shape as Line & { request_time?: string; originPointId?: string; destPointId?: string };
          const timeValue = line.time ?? (line as any).request_time;
          const timeType = typeof timeValue === 'number' ? 'number' : typeof timeValue === 'string' ? 'string' : undefined;
          placeMarks.push({
            ...baseMark,
            type: "line" as const,
            geometry: shape.coordinates,
            colour: line.colour,
            borderColour: line.borderColour,
            opacity: line.opacity,
            borderWidth: line.borderWidth,
            // Ride/trip fields for persistence
            time: timeValue ?? undefined,
            request_time: (line.request_time ?? (typeof timeValue === 'string' ? timeValue : undefined)) ?? undefined,
            numPassengers: line.numPassengers ?? undefined,
            wheelchair: line.wheelchair ?? undefined,
            ride_id: line.ride_id ?? undefined,
            user_id: line.user_id ?? undefined,
            standard_seat: line.standard_seat ?? undefined,
            request_type: line.request_type ?? undefined,
            originPointId: line.originPointId ?? undefined,
            destPointId: line.destPointId ?? undefined,
            _timeType: timeType
          });
        }
      });
    
    console.log(`💾 Converting layer "${layer.layerName}": ${layer.shapes.length} shapes → ${placeMarks.length} placeMarks`);
    
    return {
      layerName: layer.layerName,
      placeMarks
    };
  });
}

/**
 * Generate ExtendedData XML from placemark properties
 */
function generateExtendedData(mark: LegacyPlaceMark): string {
  const dataElements: string[] = [];
  
  const escapeXML = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };
  
  // Persist shape ID - critical for originPointId/destPointId references on load
  if (mark.id) {
    dataElements.push(`<Data name="id"><value>${escapeXML(mark.id)}</value></Data>`);
  }
  if (mark.colour) {
    dataElements.push(`<Data name="colour"><value>${escapeXML(mark.colour)}</value></Data>`);
  }
  if (mark.borderColour) {
    dataElements.push(`<Data name="borderColour"><value>${escapeXML(mark.borderColour)}</value></Data>`);
  }
  if (mark.opacity) {
    dataElements.push(`<Data name="opacity"><value>${escapeXML(mark.opacity)}</value></Data>`);
  }
  if (mark.borderWidth !== undefined) {
    dataElements.push(`<Data name="borderWidth"><value>${mark.borderWidth}</value></Data>`);
  }
  if (mark.icon) {
    dataElements.push(`<Data name="icon"><value>${escapeXML(mark.icon)}</value></Data>`);
  }
  if (mark.size !== undefined) {
    dataElements.push(`<Data name="size"><value>${mark.size}</value></Data>`);
  }
  // Ride/trip fields (for line type) - all values escaped for XML safety
  if (mark.time !== undefined && mark.time !== null && mark.time !== '') {
    dataElements.push(`<Data name="time"><value>${escapeXML(String(mark.time))}</value></Data>`);
  }
  if (mark.request_time) {
    dataElements.push(`<Data name="request_time"><value>${escapeXML(mark.request_time)}</value></Data>`);
  }
  if (mark.numPassengers !== undefined && mark.numPassengers !== null && mark.numPassengers !== '') {
    dataElements.push(`<Data name="numPassengers"><value>${escapeXML(String(mark.numPassengers))}</value></Data>`);
  }
  if (mark.wheelchair !== undefined && mark.wheelchair !== null && mark.wheelchair !== '') {
    dataElements.push(`<Data name="wheelchair"><value>${escapeXML(String(mark.wheelchair))}</value></Data>`);
  }
  if (mark.ride_id) {
    dataElements.push(`<Data name="ride_id"><value>${escapeXML(mark.ride_id)}</value></Data>`);
  }
  if (mark.user_id) {
    dataElements.push(`<Data name="user_id"><value>${escapeXML(mark.user_id)}</value></Data>`);
  }
  if (mark.standard_seat !== undefined) {
    dataElements.push(`<Data name="standard_seat"><value>${escapeXML(String(mark.standard_seat))}</value></Data>`);
  }
  if (mark.request_type) {
    dataElements.push(`<Data name="request_type"><value>${escapeXML(mark.request_type)}</value></Data>`);
  }
  if (mark.originPointId) {
    dataElements.push(`<Data name="originPointId"><value>${escapeXML(mark.originPointId)}</value></Data>`);
  }
  if (mark.destPointId) {
    dataElements.push(`<Data name="destPointId"><value>${escapeXML(mark.destPointId)}</value></Data>`);
  }
  if (mark._timeType) {
    dataElements.push(`<Data name="_timeType"><value>${escapeXML(mark._timeType)}</value></Data>`);
  }
  
  if (dataElements.length === 0) {
    return '';
  }
  
  return `
    <ExtendedData>
      ${dataElements.join('\n      ')}
    </ExtendedData>`;
}

/**
 * Legacy create_kml function that matches the old system exactly
 * This is the function the backend expects
 * Now includes fleet data and fixed routes support
 */
export default function create_kml(
  layers: Layer[], 
  mapName: string, 
  fleets: Fleet[] = [],
  fixedRoutes?: { routes: FixedRoute[]; stops: FixedRouteStop[] }
): string {
  const legacyLayers = convertToLegacyLayers(layers);
  
  const xmlHeader = `<?xml version="1.0" encoding="UTF-8"?>`;
  const kmlStart = `<kml xmlns="http://www.opengis.net/kml/2.2">`;
  const documentStart = `<Document><name>${mapName}</name><description>Description of your KML file</description>`;

  let documentLayers = [];
  for (const layer of legacyLayers) {
    const layerName = `<name>${layer.layerName}</name>`;
    let marks = [];
    
    for (const mark of layer.placeMarks) {
      let myMark = '';
      if (mark.type === "point") {
        // Validate point data before creating KML
        if (typeof mark.latitude !== 'number' || typeof mark.longitude !== 'number' || 
            isNaN(mark.latitude) || isNaN(mark.longitude)) {
          console.warn(`⚠️ Skipping invalid point "${mark.label}": lat=${mark.latitude}, lng=${mark.longitude}`);
          continue;
        }
        // Escape XML special characters in the label
        const escapedLabel = (mark.label || 'Unnamed')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');
        
        const extendedData = generateExtendedData(mark);
        myMark = `
          <Placemark>
            <name>${escapedLabel}</name>
            <Point>
              <coordinates>${mark.longitude},${mark.latitude},0</coordinates>
            </Point>${extendedData}
          </Placemark>
        `;
      } else if (mark.type === 'polygon') {
        // Validate polygon geometry before creating KML
        if (!mark.geometry || !Array.isArray(mark.geometry) || mark.geometry.length === 0) {
          console.warn(`⚠️ Skipping invalid polygon "${mark.label}": no geometry`);
          continue;
        }
        const coords = mark.geometry.map(pair => {
          if (!Array.isArray(pair) || pair.length < 2) {
            console.warn(`⚠️ Invalid coordinate pair in polygon "${mark.label}":`, pair);
            return null;
          }
          return `${pair[1]},${pair[0]},0`;
        }).filter(coord => coord !== null).join(' ');
        
        if (!coords || coords.trim() === '') {
          console.warn(`⚠️ Skipping polygon "${mark.label}": no valid coordinates`);
          continue;
        }

        // Escape XML special characters in the label
        const escapedLabel = (mark.label || 'Unnamed')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');
        
        const extendedData = generateExtendedData(mark);
        myMark = `
          <Placemark>
            <name>${escapedLabel}</name>
            <Polygon>
              <outerBoundaryIs>
                <LinearRing>
                  <coordinates>${coords}</coordinates>
                </LinearRing>
              </outerBoundaryIs>
            </Polygon>${extendedData}
          </Placemark>
        `;
      } else if (mark.type === 'line') {
        // Validate line geometry before creating KML
        if (!mark.geometry || !Array.isArray(mark.geometry) || mark.geometry.length === 0) {
          console.warn(`⚠️ Skipping invalid line "${mark.label}": no geometry`);
          continue;
        }
        const coords = mark.geometry.map(pair => {
          if (!Array.isArray(pair) || pair.length < 2) {
            console.warn(`⚠️ Invalid coordinate pair in line "${mark.label}":`, pair);
            return null;
          }
          return `${pair[1]},${pair[0]},0`;
        }).filter(coord => coord !== null).join(' ');
        
        if (!coords || coords.trim() === '') {
          console.warn(`⚠️ Skipping line "${mark.label}": no valid coordinates`);
          continue;
        }

        // Escape XML special characters in the label
        const escapedLabel = (mark.label || 'Unnamed')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');
        
        const extendedData = generateExtendedData(mark);
        myMark = `
          <Placemark>
            <name>${escapedLabel}</name>
            <LineString>
              <coordinates>${coords}</coordinates>
            </LineString>${extendedData}
          </Placemark>
        `;
      } else {
        console.warn(`⚠️ Skipping placemark "${mark.label}" - unknown type: ${(mark as any).type}`);
        continue;
      }
      
      if (myMark) {
        marks.push(myMark);
        // Log first few characters of generated KML for debugging
        if (marks.length === 1) {
          console.log(`  ✓ Generated KML for first placemark in "${layer.layerName}":`, myMark.substring(0, 150));
        }
      }
    }
    
    console.log(`  📝 Generated ${marks.length} Placemarks for layer "${layer.layerName}"`);
    
    const layerContent = `<Folder>${layerName}${marks.join('')}</Folder>`;
    documentLayers.push(layerContent);
  }
  
  // Count Placemarks in generated KML by counting occurrences
  const totalPlacemarks = documentLayers.reduce((sum, layer) => {
    const matches = layer.match(/<Placemark>/g);
    return sum + (matches ? matches.length : 0);
  }, 0);
  console.log(`💾 Total KML layers: ${documentLayers.length}, total placemarks: ${totalPlacemarks}`);

  const documentEnd = `</Document>`;
  const kmlEnd = `</kml>`;
  
  // Generate layer KML
  const layerKml = xmlHeader + kmlStart + documentStart + documentLayers.join('') + documentEnd + kmlEnd;
  
  // Generate fleet KML
  const fleetKml = serializeFleetToKml(fleets);
  
  // Generate fixed routes KML
  const fixedRoutesKml = fixedRoutes 
    ? serializeFixedRoutesToKml(fixedRoutes.routes, fixedRoutes.stops)
    : '';
  
  // Combine all KML data
  return combineKmlDataWithFixedRoutes(layerKml, fleetKml, fixedRoutesKml);
}

// Export the legacy function name for backward compatibility
export { create_kml }; 
